/**
 * Global fetch interceptor that adds correlation headers to all requests
 * This ensures end-to-end tracing without modifying existing code
 */

import {getAppInsights} from "./appInsights";

/**
 * Generates a unique correlation ID
 */
function generateCorrelationId(): string {
  return Array.from({length: 32}, () => Math.floor(Math.random() * 16).toString(16)).join("");
}

/**
 * Generates a span ID
 */
function generateSpanId(): string {
  return Array.from({length: 16}, () => Math.floor(Math.random() * 16).toString(16)).join("");
}

/**
 * Initialize fetch interceptor
 * Call this once during app initialization
 */
export function initializeFetchInterceptor() {
  if (typeof window === "undefined") {
    return; // Server-side, don't intercept
  }

  // Check if already initialized
  if ((window as any).__fetchInterceptorInitialized) {
    return;
  }

  // Store original fetch
  const originalFetch = window.fetch;

  // Override global fetch
  window.fetch = async function interceptedFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    // Only add headers to API calls (not external resources)
    const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
    
    // Check if this is an API call:
    // 1. Contains /api/ in the path
    // 2. Starts with /api (relative URL)
    // 3. Goes to api.dev.edupop.nl or api.edupop.nl (backend API)
    const isApiCall = url.includes("/api/") || 
                      url.startsWith("/api") || 
                      url.includes("api.dev.edupop.nl") || 
                      url.includes("api.edupop.nl");

    if (isApiCall) {
      const headers = new Headers(init?.headers || {});

      // Check if headers already exist
      const hasRequestId = headers.has("Request-Id") || headers.has("request-id");

      // Only add headers if not already present
      if (!hasRequestId) {
        try {
          const appInsights = getAppInsights();
          let traceId: string;
          let spanId: string;

          if (appInsights?.context?.telemetryTrace) {
            const trace = appInsights.context.telemetryTrace as any;

            traceId = trace.traceID || trace.traceId || generateCorrelationId();
            spanId = trace.spanID || trace.spanId || generateSpanId();
          } else {
            traceId = generateCorrelationId();
            spanId = generateSpanId();
          }

          // Add correlation headers
          headers.set("traceparent", `00-${traceId}-${spanId}-01`);
          headers.set("Request-Id", `|${traceId}.${spanId}.`);

          // Update init with new headers
          init = {
            ...init,
            headers,
          };
        } catch (error) {
          console.warn("[FETCH-INTERCEPT] Failed to add correlation headers:", error);
        }
      }
    }

    // Call original fetch
    return originalFetch(input, init);
  };

  // Mark as initialized
  (window as any).__fetchInterceptorInitialized = true;
}
